import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import AssetNav from "../components/AssetNav";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import FileUpload from "../components/FileUpload";
import { OwnedBreakdown } from "../components/OwnedBreakdown";
import { ItemEditDialog } from "../components/ItemEditDialog";
import SetupGuide from "../components/SetupGuide";
import { ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth, hasModuleAccess, hasRole } from "../lib/auth";
import { SUGGESTED_UOMS } from "../lib/uom";

const MODES = { tagged: "Individually Tagged", bulk: "Bulk Reusable", consumable: "Consumable" };

export default function AssetMaster() {
  const { user } = useAuth();
  const isAdmin = hasRole(user, "admin");
  const canManage = hasModuleAccess(user, "asset_management", "manage");
  const [items, setItems] = useState([]);
  const [owned, setOwned] = useState({});
  const [cats, setCats] = useState([]);
  const [depts, setDepts] = useState([]);
  const [uoms, setUoms] = useState({ suggested: SUGGESTED_UOMS, custom: [] });
  const [opening, setOpening] = useState({ finalized: false });
  const [rooms, setRooms] = useState([]);
  const [draft, setDraft] = useState({});      // item_id -> {opening_qty, allocation}
  const [newItem, setNewItem] = useState({ sku: "", name: "", category_id: "", department_id: "", tracking_mode: "consumable", unit: "pcs", base_uom: "", issue_scope: "individual", min_level: 0, tag_prefix: "" });
  const [photoItem, setPhotoItem] = useState(null);   // item whose photo dialog is open
  const [flt, setFlt] = useState({ q: "", dept: "all", cat: "all", mode: "all", status: "active" });
  const [selected, setSelected] = useState(new Set());
  const [bulkDept, setBulkDept] = useState("");
  const [bulkUom, setBulkUom] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    const [i, c, d, o, r, u, reg] = await Promise.all([
      api.get("/assets/items"), api.get("/assets/categories"), api.get("/assets/departments"),
      api.get("/assets/opening/status"), api.get("/rooms"), api.get("/assets/uoms"),
      api.get("/assets/reports/register"),
    ]);
    setItems(i.data); setCats(c.data); setDepts(d.data); setOpening(o.data); setRooms(r.data);
    setUoms(u.data || { suggested: SUGGESTED_UOMS, custom: [] });
    const om = {};
    (reg.data?.items || []).forEach(x => { om[x.item_id] = x; });
    setOwned(om);
    const dr = {};
    o.data.items.forEach(it => { dr[it.id] = { opening_qty: it.opening_qty_draft || 0, allocation: it.opening_allocation_draft || [] }; });
    setDraft(dr);
  };
  useEffect(() => { load(); }, []);

  const catName = id => cats.find(c => c.id === id)?.name || "—";
  const uomOptions = [...(uoms.suggested || []), ...(uoms.custom || [])];
  const changeUom = async (it, value) => {
    try {
      await api.patch(`/assets/items/${it.id}`, { base_uom: value });
      toast.success("Base UOM updated"); load();
    } catch (e) {
      const detail = String(e.response?.data?.detail || "");
      if (detail.includes("requires a reason")) {
        const reason = window.prompt("This item has transactions. Enter a reason for changing its Base UOM (audited):");
        if (!reason) return;
        try {
          await api.patch(`/assets/items/${it.id}`, { base_uom: value, uom_change_reason: reason });
          toast.success("Base UOM updated (audited)"); load();
        } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
      } else { toast.error(formatApiErrorDetail(detail)); }
    }
  };
  const deptName = id => depts.find(d => d.id === id)?.name || "Not Assigned";
  const deptOptions = (it) => {
    const active = depts.filter(d => d.active);
    if (it.department_id && !active.some(d => d.id === it.department_id)) {
      const cur = depts.find(d => d.id === it.department_id);
      if (cur) return [cur, ...active];
    }
    return active;
  };
  const filtered = items.filter(it => {
    if (flt.status === "active" && !it.active) return false;
    if (flt.status === "inactive" && it.active) return false;
    if (flt.dept !== "all" && it.department_id !== flt.dept) return false;
    if (flt.cat !== "all" && it.category_id !== flt.cat) return false;
    if (flt.mode !== "all" && it.tracking_mode !== flt.mode) return false;
    if (flt.q && !(`${it.name} ${it.sku}`.toLowerCase().includes(flt.q.toLowerCase()))) return false;
    return true;
  });

  const addItem = async () => {
    if (!newItem.sku || !newItem.name || !newItem.category_id || !newItem.department_id) return toast.error("SKU, name, category and department are required");
    try {
      await api.post("/assets/items", { ...newItem, min_level: Number(newItem.min_level), base_uom: newItem.base_uom || null, tag_prefix: newItem.tag_prefix || null });
      toast.success("Item added");
      setNewItem({ sku: "", name: "", category_id: "", department_id: "", tracking_mode: "consumable", unit: "pcs", base_uom: "", issue_scope: "individual", min_level: 0, tag_prefix: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const patchItem = async (id, changes) => {
    try { await api.patch(`/assets/items/${id}`, changes); toast.success("Item updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleSel = (id) => setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkAssign = async () => {
    if (!bulkDept) return toast.error("Choose a department");
    const ids = [...selected];
    if (!ids.length) return;
    const dn = depts.find(d => d.id === bulkDept)?.name;
    if (!window.confirm(`Assign ${ids.length} item(s) to "${dn}"? This overwrites their current department.`)) return;
    try {
      const { data } = await api.post("/assets/items/bulk-department", { item_ids: ids, department_id: bulkDept });
      toast.success(`Assigned ${data.updated} item(s) to ${dn}`); setSelected(new Set()); setBulkDept(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const bulkSetUom = async () => {
    if (!bulkUom) return toast.error("Choose a Base UOM");
    const ids = [...selected];
    if (!ids.length) return;
    const changing = items.some(i => ids.includes(i.id) && i.base_uom && i.base_uom !== bulkUom);
    let reason = null;
    if (changing) {
      reason = window.prompt("Some selected items already have a Base UOM. Enter a reason to change them (audited):");
      if (!reason) return;
    }
    try {
      const { data } = await api.post("/assets/items/bulk-uom", { item_ids: ids, base_uom: bulkUom, reason });
      toast.success(`Base UOM set on ${data.updated} item(s)${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      setSelected(new Set()); setBulkUom(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveOpening = async () => {
    const lines = Object.entries(draft).map(([item_id, v]) => ({
      item_id, opening_qty: Number(v.opening_qty || 0),
      allocation: (v.allocation || []).filter(a => Number(a.qty) > 0).map(a => ({ room_id: a.room_id, qty: Number(a.qty) })),
    }));
    try { await api.post("/assets/opening/save", lines); toast.success("Opening draft saved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const finalizeOpening = async () => {
    if (!window.confirm("Finalize opening stock? Entries become locked; corrections then require audited adjustments.")) return;
    try {
      const { data } = await api.post("/assets/opening/finalize");
      toast.success(`Opening finalized — ${data.units_created} tagged units, ${data.ledger_lines} ledger lines`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setAlloc = (itemId, idx, field, value) => {
    setDraft(prev => {
      const cur = { ...(prev[itemId] || { opening_qty: 0, allocation: [] }) };
      const alloc = [...(cur.allocation || [])];
      alloc[idx] = { ...alloc[idx], [field]: value };
      cur.allocation = alloc;
      return { ...prev, [itemId]: cur };
    });
  };

  return (
    <Layout>
      <div className="mb-4"><h1 className="section-title">Asset & Inventory Master</h1></div>
      <AssetNav />
      <Tabs defaultValue="items">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="items" data-testid="tab-asset-items">Items</TabsTrigger>
          <TabsTrigger value="opening" data-testid="tab-asset-opening">Opening Stock Setup</TabsTrigger>
        </TabsList>

        <TabsContent value="items">
          <Card className="p-3 mb-3 flex flex-wrap gap-2 items-end" data-testid="item-master-filters">
            <div><Label className="text-xs">Search</Label><Input value={flt.q} onChange={e => setFlt({ ...flt, q: e.target.value })} placeholder="Name, SKU or Asset ID" className="h-9 mt-1 w-52" data-testid="im-search" /></div>
            <div><Label className="text-xs">Department</Label>
              <Select value={flt.dept} onValueChange={v => setFlt({ ...flt, dept: v })}><SelectTrigger className="h-9 mt-1 w-40" data-testid="im-dept"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Departments</SelectItem>{depts.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-xs">Category</Label>
              <Select value={flt.cat} onValueChange={v => setFlt({ ...flt, cat: v })}><SelectTrigger className="h-9 mt-1 w-40" data-testid="im-cat"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Categories</SelectItem>{cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-xs">Tracking</Label>
              <Select value={flt.mode} onValueChange={v => setFlt({ ...flt, mode: v })}><SelectTrigger className="h-9 mt-1 w-40" data-testid="im-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Modes</SelectItem>{Object.entries(MODES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 h-9 px-2">
              <input type="checkbox" checked={flt.status !== "active"} onChange={e => setFlt({ ...flt, status: e.target.checked ? "all" : "active" })} data-testid="im-show-inactive" /> Show inactive
            </label>
          </Card>
          {canManage && (
            <Card className="p-4 mb-3">
              <div className="grid grid-cols-2 md:grid-cols-8 gap-3 items-end">
                <div><Label>SKU</Label><Input value={newItem.sku} onChange={e => setNewItem({ ...newItem, sku: e.target.value.toUpperCase() })} placeholder="VI-AMN-XXX" className="h-11 mt-1" data-testid="new-asset-sku" /></div>
                <div className="col-span-2"><Label>Name</Label><Input value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })} className="h-11 mt-1" data-testid="new-asset-name" /></div>
                <div><Label>Category</Label><Select value={newItem.category_id} onValueChange={v => setNewItem({ ...newItem, category_id: v })}><SelectTrigger className="h-11 mt-1" data-testid="new-asset-category"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Department</Label><Select value={newItem.department_id} onValueChange={v => setNewItem({ ...newItem, department_id: v })}><SelectTrigger className="h-11 mt-1" data-testid="new-asset-department"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{depts.filter(d => d.active).map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Tracking</Label><Select value={newItem.tracking_mode} onValueChange={v => setNewItem({ ...newItem, tracking_mode: v })}><SelectTrigger className="h-11 mt-1" data-testid="new-asset-tracking"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(MODES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Min Level</Label><Input type="number" value={newItem.min_level} onChange={e => setNewItem({ ...newItem, min_level: e.target.value })} className="h-11 mt-1" /></div>
                {newItem.tracking_mode !== "tagged" && (
                  <div><Label>Base UOM</Label><Select value={newItem.base_uom} onValueChange={v => setNewItem({ ...newItem, base_uom: v })}><SelectTrigger className="h-11 mt-1" data-testid="new-asset-uom"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{uomOptions.map(u => <SelectItem key={u.name} value={u.name}>{u.name}</SelectItem>)}</SelectContent></Select></div>
                )}
                {newItem.tracking_mode !== "tagged" && (
                  <div><Label>Issue Scope</Label><Select value={newItem.issue_scope} onValueChange={v => setNewItem({ ...newItem, issue_scope: v })}><SelectTrigger className="h-11 mt-1" data-testid="new-asset-scope"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="individual">Individual Room</SelectItem><SelectItem value="shared">All Rooms / Housekeeping Common Use</SelectItem></SelectContent></Select></div>
                )}
                <div><Button onClick={addItem} className="h-11 w-full bg-slate-900 text-white hover:bg-slate-800" data-testid="add-asset-item">Add Item</Button></div>
              </div>
              {newItem.tracking_mode === "tagged" && (
                <div className="mt-3 max-w-xs"><Label>Tag Prefix (e.g. KET → VI-KET-0001)</Label><Input value={newItem.tag_prefix} onChange={e => setNewItem({ ...newItem, tag_prefix: e.target.value.toUpperCase() })} className="h-11 mt-1" /></div>
              )}
            </Card>
          )}
          {canManage && selected.size > 0 && (
            <Card className="p-3 mb-3 flex flex-wrap items-center gap-2 border-slate-300 bg-slate-50" data-testid="bulk-dept-bar">
              <div className="text-sm font-medium text-slate-700">{selected.size} item(s) selected</div>
              <Select value={bulkDept} onValueChange={setBulkDept}>
                <SelectTrigger className="h-9 w-52" data-testid="bulk-dept-select"><SelectValue placeholder="Assign to department…" /></SelectTrigger>
                <SelectContent>{depts.filter(d => d.active).map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={bulkAssign} className="h-9 bg-slate-900 text-white hover:bg-slate-800" data-testid="bulk-dept-apply">Assign</Button>
              <span className="text-slate-300">|</span>
              <Select value={bulkUom} onValueChange={setBulkUom}>
                <SelectTrigger className="h-9 w-52" data-testid="bulk-uom-select"><SelectValue placeholder="Set Base UOM…" /></SelectTrigger>
                <SelectContent>{uomOptions.map(u => <SelectItem key={u.name} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={bulkSetUom} className="h-9 bg-slate-900 text-white hover:bg-slate-800" data-testid="bulk-uom-apply">Set UOM</Button>
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} data-testid="bulk-dept-clear">Clear</Button>
            </Card>
          )}
          <Card className="p-0 overflow-x-auto">
            <table className="vr-table" data-testid="asset-items-table">
              <thead><tr>{canManage && <th className="w-8"><input type="checkbox" checked={filtered.length > 0 && filtered.every(i => selected.has(i.id))} onChange={e => setSelected(e.target.checked ? new Set(filtered.map(i => i.id)) : new Set())} data-testid="im-select-all" /></th>}<th>Photo</th><th>SKU</th><th>Name</th><th>Category</th><th>Department</th><th>Tracking</th><th>Unit</th><th>Base UOM</th><th>Issue Scope</th><th className="num">Min</th><th className="num">Owned Total</th><th>Status</th>{canManage && <th></th>}</tr></thead>
              <tbody>
                {filtered.map(it => (
                  <tr key={it.id}>
                    {canManage && <td><input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleSel(it.id)} data-testid={`im-select-${it.sku}`} /></td>}
                    <td>
                      {it.photo_url ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${it.photo_url}`} alt={it.name}
                          className="w-10 h-10 object-cover rounded border border-slate-200 cursor-pointer"
                          onClick={() => canManage && setPhotoItem(it)} data-testid={`photo-thumb-${it.sku}`} />
                      ) : canManage ? (
                        <button className="w-10 h-10 rounded border border-dashed border-slate-300 flex items-center justify-center text-slate-400 hover:border-slate-500"
                          onClick={() => setPhotoItem(it)} data-testid={`photo-add-${it.sku}`} title="Add reference photo">
                          <ImageIcon className="w-4 h-4" />
                        </button>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="font-mono text-xs">{it.sku}</td><td>{it.name}</td><td className="text-xs">{catName(it.category_id)}</td>
                    <td className="text-xs">
                      {canManage ? (
                        <Select value={it.department_id || ""} onValueChange={v => patchItem(it.id, { department_id: v })}>
                          <SelectTrigger className="h-9 w-44" data-testid={`dept-select-${it.sku}`}><SelectValue placeholder="Not Assigned" /></SelectTrigger>
                          <SelectContent>{deptOptions(it).map(d => <SelectItem key={d.id} value={d.id}>{d.name}{!d.active ? " (inactive)" : ""}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : <span className={it.department_id ? "" : "text-slate-400"}>{deptName(it.department_id)}</span>}
                    </td>
                    <td><Badge className="bg-slate-100 border text-slate-700">{MODES[it.tracking_mode]}</Badge></td>
                    <td>{it.unit}</td>
                    <td className="text-xs">
                      {it.tracking_mode === "tagged" ? <span className="text-slate-300">—</span> :
                        canManage ? (
                          <Select value={it.base_uom || ""} onValueChange={v => changeUom(it, v)}>
                            <SelectTrigger className={`h-9 w-40 ${!it.base_uom ? "border-amber-400 text-amber-700" : ""}`} data-testid={`uom-select-${it.sku}`}><SelectValue placeholder="UOM Setup Required" /></SelectTrigger>
                            <SelectContent>{uomOptions.map(u => <SelectItem key={u.name} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (it.base_uom ? it.base_uom : <Badge className="bg-amber-100 border text-amber-800">UOM Setup Required</Badge>)}
                    </td>
                    <td className="text-xs">
                      {it.tracking_mode === "tagged" ? <span className="text-slate-300">—</span> :
                        canManage ? (
                          <Select value={it.issue_scope || "individual"} onValueChange={v => patchItem(it.id, { issue_scope: v })}>
                            <SelectTrigger className="h-9 w-36" data-testid={`scope-select-${it.sku}`}><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="individual">Individual Room</SelectItem><SelectItem value="shared">All Rooms / Common</SelectItem></SelectContent>
                          </Select>
                        ) : (it.issue_scope === "shared" ? "All Rooms / Common" : "Individual Room")}
                    </td>
                    <td className="num">{it.min_level || 0}</td>
                    <td className="num"><OwnedBreakdown total={owned[it.id]?.owned_total ?? 0} canView rows={owned[it.id] ? [
                      { label: "Store", qty: owned[it.id].store },
                      { label: "Rooms", qty: owned[it.id].rooms },
                      { label: "Pool / Common", qty: owned[it.id].pool },
                      { label: "Repair", qty: owned[it.id].repair },
                      { label: "Damaged", qty: owned[it.id].damaged },
                    ] : []} testid={`asset-owned-${it.sku}`} /></td>
                    <td>{it.archived ? <Badge className="bg-amber-100 border text-amber-800">Archived</Badge> : it.active ? <Badge className="bg-emerald-100 border text-emerald-800">Active</Badge> : <Badge className="bg-slate-100 border text-slate-600">Inactive</Badge>}</td>
                    {canManage && <td><Button size="sm" variant="outline" onClick={() => { setEditItem(it); setEditOpen(true); }} data-testid={`edit-asset-${it.sku}`}>Edit</Button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <ItemEditDialog
            open={editOpen} onOpenChange={setEditOpen} item={editItem} apiBase="/assets/items"
            title="Edit Asset / Consumable Item"
            fields={[{ key: "name", label: "Name" }, { key: "unit", label: "Unit" }, { key: "min_level", label: "Min Level", type: "number" }, { key: "remarks", label: "Remarks" }]}
            onSaved={load}
            canDelete={isAdmin}
          />
        </TabsContent>

        <TabsContent value="opening">
          {opening.finalized ? (
            <Card className="p-6 text-center">
              <div className="text-emerald-700 font-semibold mb-1">Opening stock is finalized and locked.</div>
              <div className="text-sm text-slate-500">Corrections require audited adjustments (Admin → Store Inventory → Adjustment).</div>
            </Card>
          ) : (
            <>
              <SetupGuide current="opening" />
              <Card className="p-4 mb-3 border-amber-300 bg-amber-50 text-sm text-amber-900" data-testid="opening-draft-note">
                <strong>Draft / Setup Pending.</strong> 20 items begin at zero/draft; Cane Basket (8) and White Toiletry Tray (13) are prefilled as confirmed draft quantities.
                Finalization requires Store + Room allocation to <strong>exactly equal</strong> each opening quantity — nothing is auto-sent to Store.
                <div className="mt-1">Note: 8 Cane Baskets + 13 White Toiletry Trays cover 21 of the 22 rooms — confirm whether one room is exempt or a quantity needs increasing before allocating.</div>
              </Card>
              <Card className="p-0 overflow-x-auto mb-3">
                <table className="vr-table" data-testid="opening-table">
                  <thead><tr><th>Item</th><th>Mode</th><th className="num">Opening Qty (Draft)</th><th>Room Allocation</th></tr></thead>
                  <tbody>
                    {items.filter(i => i.active).map(it => {
                      const d = draft[it.id] || { opening_qty: 0, allocation: [] };
                      const allocated = (d.allocation || []).reduce((s, a) => s + Number(a.qty || 0), 0);
                      return (
                        <tr key={it.id}>
                          <td>{it.name}<div className="text-[10px] text-slate-400 font-mono">{it.sku}</div></td>
                          <td className="text-xs">{MODES[it.tracking_mode]}</td>
                          <td className="num">
                            <Input type="number" min="0" className="h-9 w-24 ml-auto text-right" value={d.opening_qty}
                              disabled={!canManage}
                              onChange={e => setDraft(p => ({ ...p, [it.id]: { ...d, opening_qty: e.target.value } }))}
                              data-testid={`opening-qty-${it.sku}`} />
                          </td>
                          <td>
                            <div className="space-y-1">
                              {(d.allocation || []).map((a, idx) => (
                                <div key={a.room_id || idx} className="flex gap-2 items-center">
                                  <Select value={a.room_id || ""} disabled={!canManage} onValueChange={v => setAlloc(it.id, idx, "room_id", v)}>
                                    <SelectTrigger className="h-8 w-28 text-xs"><SelectValue placeholder="Location" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="STORE">Store</SelectItem>
                                      {rooms.map(r => <SelectItem key={r.id} value={r.id}>Room {r.number}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                  <Input type="number" min="0" className="h-8 w-16 text-right" value={a.qty || 0} disabled={!canManage} onChange={e => setAlloc(it.id, idx, "qty", e.target.value)} />
                                </div>
                              ))}
                              {canManage && (
                                <button className="text-xs text-slate-500 underline" onClick={() => setDraft(p => ({ ...p, [it.id]: { ...d, allocation: [...(d.allocation || []), { room_id: "", qty: 0 }] } }))}>+ allocation line</button>
                              )}
                              {Number(d.opening_qty || 0) > 0 && (
                                <div className={`text-[10px] ${allocated === Number(d.opening_qty || 0) ? "text-emerald-600" : "text-red-600 font-semibold"}`} data-testid={`alloc-status-${it.sku}`}>
                                  Allocated {allocated} of {d.opening_qty}{allocated !== Number(d.opening_qty || 0) ? " — must match to finalize" : " ✓"}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Card>
              {canManage && (
                <div className="flex gap-3">
                  <Button variant="outline" onClick={saveOpening} data-testid="save-opening-draft">Save Draft</Button>
                  <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={finalizeOpening} data-testid="finalize-opening">Finalize & Lock Opening</Button>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!photoItem} onOpenChange={() => setPhotoItem(null)}>
        <DialogContent data-testid="photo-dialog">
          <DialogHeader><DialogTitle>{photoItem?.name} — Reference Photo</DialogTitle></DialogHeader>
          {photoItem?.photo_url && (
            <img src={`${process.env.REACT_APP_BACKEND_URL}${photoItem.photo_url}`} alt={photoItem.name}
              className="w-full max-h-64 object-contain rounded border border-slate-200 bg-slate-50" />
          )}
          <FileUpload
            value={photoItem?.photo_url || null}
            testid="item-photo-upload"
            onChange={async (url) => {
              try {
                await api.patch(`/assets/items/${photoItem.id}`, { photo_url: url || "" });
                toast.success(url ? "Photo saved" : "Photo removed");
                setPhotoItem(p => ({ ...p, photo_url: url }));
                load();
              } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
            }}
          />
          <div className="text-xs text-slate-400">Upload your own photo of the item (JPG/PNG). Used as a visual reference only.</div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
